import type { FormDetailResponse } from "@/types/entities/formality-request";

export type FieldList =
  FormDetailResponse["steps"][number]["sections"][number]["fields"];

/** Conditional fields that were never filled are not part of a correction. */
export function filterVisibleFields(fields: FieldList): FieldList {
  return fields.filter((field) => {
    const isConditionalField = (field as { is_conditional?: boolean })
      .is_conditional;
    if (
      isConditionalField &&
      (!field.filled_fields || field.filled_fields.length === 0)
    ) {
      return false;
    }
    return true;
  });
}

export function getVisibleSections(step: FormDetailResponse["steps"][number]) {
  return step.sections.filter((section) =>
    section.fields.some(
      (field) => field.filled_fields && field.filled_fields.length > 0,
    ),
  );
}

/**
 * Keeps only the steps that have something to review and precomputes the global
 * section/field offsets each one starts at, so numbering stays continuous
 * across the steps that were filtered out.
 */
export function buildVisibleSteps(data: FormDetailResponse | undefined) {
  if (!data?.steps) return [];

  let globalSectionIndex = 0;
  let globalFieldIndex = 0;

  return data.steps
    .map((step) => ({ step, visibleSections: getVisibleSections(step) }))
    .filter(({ visibleSections }) => visibleSections.length > 0)
    .map(({ step, visibleSections }, visibleStepIndex) => {
      const sectionStartIndex = globalSectionIndex;
      const fieldStartIndex = globalFieldIndex;

      for (const section of visibleSections) {
        globalSectionIndex++;
        globalFieldIndex += filterVisibleFields(section.fields).length;
      }

      return {
        step,
        stepNumber: visibleStepIndex + 1,
        visibleSections,
        sectionStartIndex,
        fieldStartIndex,
      };
    });
}
